import { useLocation, useNavigate } from 'react-router';
import { useEffect, useRef } from 'react';
import { useElementsCallback } from './useElementsCallback';
import { LayoutClassName } from '../widgets/layout/LayoutClassName';
import { getHeadingText } from '../utils/getHeadingText';
import { getHeadingSlug } from '../utils/getHeadingSlug';

export interface UseDeepLinkingOptions {
  forwardRef: React.RefObject<HTMLDivElement>;
  selectors?: string[];
  linkOnClick?: boolean;
}

export const useDeepLinking = (options: UseDeepLinkingOptions) => {
  const { forwardRef, selectors = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'], linkOnClick = true } = options;
  const navigate = useNavigate();
  const location = useLocation();
  const skipNextHashAlignmentRef = useRef(false);

  const scrollToHeading = (element: HTMLElement, behavior: ScrollBehavior = 'auto') => {
    const scrollContainer = document.querySelector<HTMLElement>(`.${LayoutClassName.SCROLL_CONTAINER}`);
    const header = document.querySelector<HTMLElement>(`.${LayoutClassName.HEADER}`);

    if (!scrollContainer || !header) {
      return;
    }

    scrollContainer.scrollTo({
      top:
        scrollContainer.scrollTop +
        element.getBoundingClientRect().top -
        scrollContainer.getBoundingClientRect().top -
        header.offsetHeight -
        20,
      behavior
    });
  };

  useElementsCallback<HTMLElement>({
    forwardRef,
    selectors,
    deps: [location.pathname, location.hash, navigate],
    onElement: (element) => {
      const text = getHeadingText(element);
      const isPageTitle = element.tagName.toLowerCase() === 'h1';
      let selected: string | null = null;
      try {
        selected = location.hash !== '' ? decodeURIComponent(location.hash.substring(1)) : null;
      } catch {
        // Ignore malformed fragments rather than preventing the article from rendering.
      }
      const slug = getHeadingSlug(text);
      const unnumberedText = text.replace(/^\d+(?:\.\d+)*\.?\s+/, '');
      const unnumberedSlug = getHeadingSlug(unnumberedText);

      let alignmentTimeout: ReturnType<typeof setTimeout> | undefined;
      if (
        !isPageTitle &&
        selected &&
        [element.id, text, slug, unnumberedText, unnumberedSlug].includes(selected) &&
        !skipNextHashAlignmentRef.current
      ) {
        alignmentTimeout = setTimeout(() => {
          scrollToHeading(element);
        }, 100);
      }

      const listener = () => {
        skipNextHashAlignmentRef.current = true;

        if (isPageTitle) {
          const scrollContainer = document.querySelector<HTMLElement>(`.${LayoutClassName.SCROLL_CONTAINER}`);
          scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
          navigate(
            {
              pathname: location.pathname,
              search: location.search,
              hash: ''
            },
            { replace: true }
          );
          return;
        }

        scrollToHeading(element, 'smooth');
        navigate({
          pathname: location.pathname,
          search: location.search,
          hash: `#${encodeURIComponent(element.id || slug)}`
        });
      };

      if (linkOnClick) element.addEventListener('click', listener);

      return () => {
        clearTimeout(alignmentTimeout);
        element.removeEventListener('click', listener);
      };
    }
  });

  useEffect(() => {
    if (!skipNextHashAlignmentRef.current) {
      return;
    }

    skipNextHashAlignmentRef.current = false;
  }, [location.hash]);
};
